import React, { useEffect, useState } from "react";
import {
  Box, Typography, Grid, Card, CardContent, Skeleton, Alert, useTheme,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { api } from "../../api/client.js";
import { BRAND, CHART_COLORS } from "../../theme/index.js";

// ── Types ─────────────────────────────────────────────────────────────────────

interface InsightsData {
  total_sessions:   number;
  days:             number;
  has_geo:          boolean;
  by_country:       { country_code: string; sessions: number }[];
  by_region:        { country_code: string; region_code: string; region_name: string; sessions: number }[];
  by_origin_domain: { domain: string; sessions: number }[];
  by_device:        { device: string; sessions: number }[];
  by_browser:       { browser: string; sessions: number }[];
  session_depth:    { bucket: string; sessions: number }[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const COUNTRY_NAMES: Record<string, string> = {
  US: "United States", GB: "United Kingdom", CA: "Canada", AU: "Australia",
  DE: "Germany", FR: "France", NL: "Netherlands", IN: "India", BR: "Brazil",
  MX: "Mexico", JP: "Japan", SG: "Singapore", IE: "Ireland", SE: "Sweden",
  NO: "Norway", DK: "Denmark", FI: "Finland", NZ: "New Zealand", ZA: "South Africa",
  ES: "Spain", IT: "Italy", PL: "Poland", CH: "Switzerland", AT: "Austria",
};
const countryName = (code: string) => COUNTRY_NAMES[code] ?? code;

// ── Shared chart card ─────────────────────────────────────────────────────────

function ChartCard({ title, subtitle, children, height = 220 }: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  height?: number;
}) {
  const theme = useTheme();
  const dotOpacity = theme.palette.mode === "dark" ? 0.04 : 0.06;
  const dotColor = theme.palette.mode === "dark"
    ? `rgba(255,255,255,${dotOpacity})` : `rgba(0,0,0,${dotOpacity})`;

  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h5" mb={subtitle ? 0.25 : 2}>{title}</Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            {subtitle}
          </Typography>
        )}
        <Box sx={{
          height,
          backgroundImage: `radial-gradient(circle, ${dotColor} 1px, transparent 1px)`,
          backgroundSize: "20px 20px",
          borderRadius: "8px",
          padding: "4px",
        }}>
          {children}
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Charts ────────────────────────────────────────────────────────────────────

function HorizBarChart({ data, dataKey, nameKey, color }: {
  data: Record<string, any>[];
  dataKey: string;
  nameKey: string;
  color: string;
}) {
  const theme = useTheme();
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 24, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
        <YAxis type="category" dataKey={nameKey} tick={{ fontSize: 11 }} width={110} />
        <Tooltip contentStyle={{ fontSize: 12 }} />
        <Bar dataKey={dataKey} name="Sessions" fill={color} radius={[0, 3, 3, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function DeviceChart({ data }: { data: { device: string; sessions: number }[] }) {
  if (!data.length) return <NoData />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie data={data} cx="50%" cy="50%" innerRadius="45%" outerRadius="70%"
          dataKey="sessions" nameKey="device" paddingAngle={2}
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          labelLine={false}
        >
          {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Pie>
        <Tooltip formatter={(v: number) => [v, "Sessions"]} contentStyle={{ fontSize: 12 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function DepthChart({ data }: { data: { bucket: string; sessions: number }[] }) {
  const theme = useTheme();
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
        <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
        <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v: number) => [v, "Sessions"]} />
        <Bar dataKey="sessions" name="Sessions" fill={BRAND.cyan} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function NoData({ message }: { message?: string }) {
  return (
    <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Typography variant="caption" color="text.disabled">
        {message ?? "No data yet"}
      </Typography>
    </Box>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function InsightsSkeleton() {
  return (
    <Grid container spacing={2}>
      {[280, 280, 220, 220, 220, 220].map((h, i) => (
        <Grid item xs={12} md={6} key={i}>
          <Skeleton variant="rounded" height={h} />
        </Grid>
      ))}
    </Grid>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function InsightsPage() {
  const [data, setData]       = useState<InsightsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    api.get<InsightsData>("/api/v1/dashboard/insights")
      .then(setData)
      .catch(e => setError(e.message ?? "Failed to load insights"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h2">Insights</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Last 30 days · all bots
        </Typography>
      </Box>

      {loading && <InsightsSkeleton />}
      {error   && <Alert severity="error">{error}</Alert>}

      {data && (
        <Grid container spacing={2}>

          {/* ── Location ── */}
          <Grid item xs={12} md={6}>
            <ChartCard
              title="Top Countries"
              subtitle={data.has_geo ? undefined : "Geo data captured for new sessions going forward"}
              height={Math.max(180, data.by_country.length * 32)}
            >
              {data.by_country.length > 0 ? (
                <HorizBarChart
                  data={data.by_country.map(d => ({ ...d, name: countryName(d.country_code) }))}
                  dataKey="sessions"
                  nameKey="name"
                  color={BRAND.cyan}
                />
              ) : (
                <NoData message="No geo data yet — captured for new sessions" />
              )}
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ChartCard
              title="Top Regions"
              height={Math.max(180, data.by_region.length * 32)}
            >
              {data.by_region.length > 0 ? (
                <HorizBarChart
                  data={data.by_region.map(d => ({ ...d, name: `${d.region_name}, ${d.country_code}` }))}
                  dataKey="sessions"
                  nameKey="name"
                  color={BRAND.green}
                />
              ) : (
                <NoData message="No geo data yet — captured for new sessions" />
              )}
            </ChartCard>
          </Grid>

          {/* ── Device & browser ── */}
          <Grid item xs={12} md={4}>
            <ChartCard title="Device Type" height={200}>
              <DeviceChart data={data.by_device} />
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={8}>
            <ChartCard title="Browser" height={200}>
              {data.by_browser.length > 0 ? (
                <HorizBarChart
                  data={data.by_browser}
                  dataKey="sessions"
                  nameKey="browser"
                  color={BRAND.yellow}
                />
              ) : (
                <NoData />
              )}
            </ChartCard>
          </Grid>

          {/* ── Session depth ── */}
          <Grid item xs={12} md={6}>
            <ChartCard title="Session Depth" subtitle="Messages per session" height={200}>
              <DepthChart data={data.session_depth} />
            </ChartCard>
          </Grid>

          {/* ── Embed domains ── */}
          {data.by_origin_domain.length > 0 && (
            <Grid item xs={12} md={6}>
              <ChartCard
                title="Top Embedding Domains"
                subtitle="Sites where your bot is embedded"
                height={Math.max(200, data.by_origin_domain.length * 32)}
              >
                <HorizBarChart
                  data={data.by_origin_domain}
                  dataKey="sessions"
                  nameKey="domain"
                  color={BRAND.cyan}
                />
              </ChartCard>
            </Grid>
          )}

        </Grid>
      )}
    </Box>
  );
}
